import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { Region } from 'src/app/shared/models/dtos/region/region';
import { AdministratorDTO } from 'src/app/shared/models/dtos/responses/administrator-dto/administrator-dto';
import { selectableRegions } from 'src/app/utils/regions';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { MultiSelectComponent } from '../../atoms/multi-select/multi-select.component';
import { selectableRoles } from 'src/app/utils/roles';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { MatSelectChange } from '@angular/material/select';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { SecondaryContent } from '../dialog-modal/dialog-modal.component';

export interface DialogModalSelectData {
  title?: string;
  content?: string;
  admin?: AdministratorDTO;
  role?: string;
  successButtonText?: string;
  failButtonText?: string;
  isMultiSelect?: boolean;
  contentKeepWhiteSpace?: boolean;
}

@Component({
  selector: 'app-modal-select',
  templateUrl: './dialog-modal-select.component.html',
  styleUrls: ['./dialog-modal-select.component.scss'],
})
// TODO generalize component
/**
 * Modal molecule
 */
export class DialogModalSelectComponent implements OnInit {
  @ViewChild('regionMultiSelect') regionMultiSelect!: MultiSelectComponent;
  availableRegions = selectableRegions;
  availableRoles = selectableRoles;
  mappedRegions: SelectorData[] = [];
  previousRegions: number[] = [];
  role = this.data.role;

  readonly labels = {
    promoteGlobalAdmin: 'promote-to-global-admin',
    demoteGlobalAdmin: 'demote-to-global-admin',
    warnGlobalAdminPromotion: 'global-admin-promotion-warning',
    warnGlobalAdminDemotion: 'global-admin-demotion-warning',
    globalAdminAmountLimit: 'global-admin-amount-limit',
    globalAdminInfo: 'global-admin-info',
    regionChange: 'region-change',
    contentRegionCheckboxesEmpty: 'region-checkboxes-empty-1',
    warnAdminRegionChange: 'region-checkboxes-empty-2',
    globalAdminLimit: 'global-admin-limit',
    ok: 'ok',
  };

  /**
   * @constructor
   */
  constructor(
    private dialogRef: MatDialogRef<DialogModalSelectComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogModalSelectData,
    private readonly dialogService: DialogService,
    private translationService: TranslationService
  ) {}

  /**
   * ng on init
   */
  ngOnInit() {
    this.mappedRegions = this.mapRegions(this.data.admin?.regions ?? []);
    this.previousRegions = this.mappedRegions.map((region) => region.value);
  }

  /**
   * Returns regions formated for selectorData
   * @param {Region[]} regions
   * @return {SelectorData[]}
   */
  mapRegions(regions: Region[]): SelectorData[] {
    return regions.map((region) => ({
      label: region.name,
      value: region.id,
    }));
  }

  /**
   * @param {Region} region region to extract name from
   * @return {string} extracted region name
   */
  regionName(region: Region): string {
    return region.name;
  }

  /**
   * Updates region value for admin List
   * @param {MatSelectChange} event
   */
  async handleRegionChange(event: MatSelectChange): Promise<void> {
    if (event.value.length === 0) {
      this.dialogService.openDialog({
        title: await this.translationService.translateString(
          this.labels.regionChange
        ),
        content: await this.translationService.translateString(
          this.labels.contentRegionCheckboxesEmpty
        ),
        secondaryContent: await this.getSecondaryRegionContent(),
        successButtonText: this.labels.ok,
      });
      event.source.writeValue(this.previousRegions);
      this.regionMultiSelect.resetMultiSelectValue(this.previousRegions);
    } else if (
      JSON.stringify(event.value) !== JSON.stringify(this.previousRegions)
    ) {
      this.mappedRegions = this.availableRegions.filter((region) =>
        event.value.includes(region.value)
      );
    }
  }

  /**
   * Action to close the dialog
   */
  onSuccessClick() {
    const selectedRegions = this.regionMultiSelect.formControl.value;
    this.dialogRef.close({
      result: MatDialogResponse.SUCCESS,
      selectedRegions,
    });
  }

  /**
   * Action to close the dialog
   */
  onFailClick() {
    this.dialogRef.close(MatDialogResponse.FAIL);
  }

  /**
   * If whitespace should be kept (pre) in dialog content
   */
  get contentKeepWhiteSpace() {
    return Boolean(this.data.contentKeepWhiteSpace);
  }

  /**
   * Returns the correct label to be used in the demotion modal
   * @return {string}
   */
  getRoleLabel(): string {
    return (
      this.availableRoles.find((role) => role.value === this.data.role)
        ?.label ?? ''
    );
  }

  /**
   * @param {MatSelectChange} event
   *
   */
  private async getSecondaryRegionContent(): Promise<SecondaryContent[]> {
    // TODO refactor styles so its less verbose
    return [
      {
        content: await this.translationService.translateString(
          this.labels.warnAdminRegionChange
        ),
        style: {
          color: 'var(--Cinzas-400, #19212B)',
          fontFamily: 'Helvetica',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '125%',
        },
      },
    ];
  }
}
